/**
 * Pure helpers used by `QueryProvider`'s global retry policy.
 *
 * Extracted into their own module so they can be unit-tested
 * without spinning up a `QueryClient` and faking timers — the
 * decision logic is the part most likely to silently regress
 * (e.g. someone treats `MiddlewareRateLimitError` as
 * `isUpstreamRateLimit` and reintroduces the retry storm).
 */

import { RateLimitError } from '@/lib/rate-limit-error';
import {
  MiddlewareRateLimitError,
  UpstreamRateLimitResponseError,
} from '@/lib/middleware-rate-limit';

/** Returns true for upstream-source rate limits (envio / RPC bubbled through our routes). */
export function isUpstreamRateLimit(error: unknown): boolean {
  // Middleware rate limits get a dedicated branch so we never count
  // them as "worth retrying" — see `query-provider.tsx` retry config
  // for the full rationale.
  if (error instanceof MiddlewareRateLimitError) return false;
  if (error instanceof UpstreamRateLimitResponseError) return true;
  if (error instanceof RateLimitError) return true;
  if (error instanceof Error) {
    const msg = error.message.toLowerCase();
    return msg.includes('429') || msg.includes('rate limit');
  }
  return false;
}

/** Returns true for ANY rate-limit signal — used to surface the overlay. */
export function isAnyRateLimit(error: unknown): boolean {
  return (
    error instanceof MiddlewareRateLimitError ||
    error instanceof UpstreamRateLimitResponseError ||
    isUpstreamRateLimit(error)
  );
}

const JITTER_FRACTION = 0.2;

/**
 * Add ±20% jitter to a backoff delay. Without jitter, queries that
 * fail at the same instant retry at the exact same future instant,
 * recreating the original burst.
 */
export function jitter(ms: number, randomFn: () => number = Math.random): number {
  const span = ms * JITTER_FRACTION;
  return Math.round(ms - span + randomFn() * 2 * span);
}

/**
 * Decide how many times to retry. Mirrors the inline `retry` callback
 * in `QueryProvider`; exported so the policy can be asserted in
 * isolation.
 *
 * Rate-limit signals (both ours and upstream) retry zero times. The
 * next `refetchInterval` tick is the only follow-up. Retrying a
 * rate-limit immediately amplifies the problem — N downstream retries
 * land inside the same backpressure window the upstream is already
 * refusing, and the user sees a retry storm in the console. A single
 * failed refetch that waits for the next tick surfaces cleanly as
 * "stale data held on screen" thanks to `placeholderData: (prev) =>
 * prev` in `query-provider.tsx`, which is what we want.
 */
export function shouldRetry(failureCount: number, error: unknown): boolean {
  if (error instanceof MiddlewareRateLimitError) return false;
  if (isUpstreamRateLimit(error)) return false;
  return failureCount < 1;
}

/**
 * Compute the next retry delay (in ms). Mirrors the inline
 * `retryDelay` callback in `QueryProvider`. Non-rate-limit errors
 * retry once with a jittered 1s base — rate-limit errors never
 * retry (see `shouldRetry`), so they don't take this path.
 */
export function nextRetryDelay(
  _attemptIndex: number,
  _error: unknown,
  randomFn: () => number = Math.random,
): number {
  return jitter(1000, randomFn);
}
